import { useEffect, useState } from 'react';
import { request } from '@/services/request';

type CustomScope = { name: string; value: string };
type DropdownDataType = { id: string; name: string };
type Component = any;

export const useExperienceScopes = (
  component?: Component,
  type?: string,
  componentTypeSelected?: any,
  componentsArr?: any
) => {
  const [factsLoading, setFactsLoading] = useState(false);
  const [selectedExperienceTypes, setSelectedExperienceTypes] = useState<
    DropdownDataType[]
  >([]);
  const [experienceTypeSelected, setExperienceTypeSelected] =
    useState<DropdownDataType | null>(null);
  const [predefinedScopes, setPredefinedScopes] = useState<
    Record<string, string>
  >({});
  const [selectedScopes, setSelectedScopes] = useState<Record<string, string>>(
    {}
  );
  const [availableScopes, setAvailableScopes] = useState<string[]>([]);
  const [customScopes, setCustomScopes] = useState<CustomScope[]>([]);

  // Initialize experience type based on component type
  useEffect(() => {
    if (component?.type.startsWith('experience.')) {
      const displayName = componentsArr?.components?.find(
        (c: any) => c.type === component.type
      )?.component_display_name;

      const experienceType = {
        id: component.type,
        name: displayName || component.type,
      };

      setExperienceTypeSelected(experienceType);
      setSelectedExperienceTypes([experienceType]);
    } else {
      // Reset if not an experience component
      setExperienceTypeSelected(null);
      setSelectedExperienceTypes([]);
    }
  }, [component?.component_name, componentsArr]);

  // Initialize scopes based on component properties
  useEffect(() => {
    if (
      component?.type.startsWith('experience.') &&
      component.properties?.scopes
    ) {
      if (component.type === 'experience.custom') {
        // Convert scopes to custom scopes format
        const customScopesFromProps = Object.entries(
          component.properties.scopes
        ).map(([name, value]) => ({ name, value: value as string }));
        setCustomScopes(
          customScopesFromProps.length > 0
            ? customScopesFromProps
            : [{ name: '', value: '' }] // Default empty custom scope
        );
        setSelectedScopes({});
      } else {
        setSelectedScopes(component.properties.scopes);
        setCustomScopes([]);
      }
    } else {
      // Reset scopes if not an experience component
      setSelectedScopes({});
      setCustomScopes([]);
    }
  }, [
    component?.component_name,
    component?.type,
    component?.properties?.scopes,
  ]);

  // Auto-select experience type when component type is selected
  useEffect(() => {
    if (
      (componentTypeSelected?.name?.toLowerCase() === 'experience' ||
        type?.startsWith('experience.')) &&
      selectedExperienceTypes.length === 0 &&
      type &&
      componentsArr?.components
    ) {
      const match = componentsArr.components.find((c: any) => c.type === type);
      if (match) {
        const selected = {
          id: match.type,
          name: match.component_display_name,
        };
        setSelectedExperienceTypes([selected]);
        setExperienceTypeSelected(selected);
      }
    }
  }, [
    componentTypeSelected,
    type,
    componentsArr,
    selectedExperienceTypes.length,
  ]);

  // Fetch scope properties
  useEffect(() => {
    const fetchScopeProperties = async () => {
      // Skip if no types selected
      if (!selectedExperienceTypes || selectedExperienceTypes.length === 0)
        return;

      setFactsLoading(true);
      try {
        const finalTypeIds = selectedExperienceTypes.map((type) => type.id) || [
          type,
        ];

        // Clear existing scopes when switching experience types
        const onlyCustomSelected = selectedExperienceTypes.every(
          (et) => et.id === 'experience.custom'
        );
        if (onlyCustomSelected) {
          setAvailableScopes([]);
          setPredefinedScopes({});
          setSelectedScopes({});
        }

        for (const typeId of finalTypeIds) {
          if (typeId !== 'experience.custom') {
            try {
              // Fetch scope properties from backend
              const data = await request.get({
                endpoint: 'metadata/scope_properties',
                queryParams: { experience_type: typeId },
              });
              const scopeData = Array.isArray(data) ? data[0] : data;
              const scopes = Object.keys(scopeData.scope_properties || {});
              const scopeProps = scopeData.scope_properties || {};
              // Update available scopes
              setAvailableScopes((prev) => {
                return Array.from(new Set([...prev, ...scopes])).filter(
                  Boolean
                );
              });
              setPredefinedScopes((prev) => ({ ...prev, ...scopeProps }));

              // Only auto-select predefined scopes if none are selected yet
              if (Object.keys(selectedScopes).length === 0) {
                const preselected: Record<string, string> = {};
                scopes.forEach((scope) => {
                  preselected[scope] = scopeProps[scope] || '';
                });
                setSelectedScopes((prevScopes) => ({
                  ...prevScopes,
                  ...preselected,
                }));
              }
            } catch (error) {
              console.log(error);
            }
          } else if (typeId === 'experience.custom') {
            const isFirstTime =
              availableScopes.length === 0 &&
              Object.keys(selectedScopes).length === 0 &&
              customScopes.length === 0;
            if (isFirstTime) {
              setCustomScopes([{ name: '', value: '' }]);
            }
          }
        }
      } finally {
        setFactsLoading(false);
      }
    };

    fetchScopeProperties();
  }, [selectedExperienceTypes, type]);

  // Converts a predefined scope to a custom scope
  const handleMakeCustom = (scopeName: string, propName: string) => {
    // Add to custom scopes
    setCustomScopes((prev) => [...prev, { name: scopeName, value: propName }]);

    // Remove from predefined scopes
    setSelectedScopes((prev) => {
      const newScopes = { ...prev };
      delete newScopes[scopeName];
      return newScopes;
    });

    setSelectedExperienceTypes((prev) => {
      const hasCustom = prev.some((et) => et.id === 'experience.custom');
      const hasCurrent = prev.some(
        (et) => et.id === experienceTypeSelected?.id
      );
      const updated = [...prev];

      if (!hasCurrent && experienceTypeSelected) {
        updated.push(experienceTypeSelected);
      }

      if (!hasCustom) {
        updated.push({ id: 'experience.custom', name: 'Custom Web/SMS' });
      }

      return updated;
    });

    if (experienceTypeSelected?.id !== 'experience.custom') {
      setExperienceTypeSelected({
        id: 'experience.custom',
        name: 'Custom Web/SMS',
      });
    }
  };

  const handleDeleteScope = (scopeName: string) => {
    // Skip if scope not found
    if (!selectedScopes[scopeName]) return;

    // Remove from selected scopes
    const newScopes = { ...selectedScopes };
    delete newScopes[scopeName];
    setSelectedScopes(newScopes);

    // Switch to custom type after deletion
    setExperienceTypeSelected({
      id: 'experience.custom',
      name: 'Custom Web/SMS',
    });

    setSelectedExperienceTypes((prevTypes) => {
      const hasCustom = prevTypes.some((t) => t.id === 'experience.custom');
      if (!hasCustom)
        return [
          ...prevTypes,
          { id: 'experience.custom', name: 'Custom Web/SMS' },
        ];
      return prevTypes;
    });
    // Initialize empty custom scope if none exist
    setCustomScopes((prevCustomScopes) => {
      if (prevCustomScopes.length > 0) return prevCustomScopes;
      return [{ name: '', value: '' }];
    });
  };

  const mergeAllScopes = () => {
    const allScopes: Record<string, string> = {};

    // Add predefined scopes
    if (selectedExperienceTypes.some((t) => t.id !== 'experience.custom')) {
      Object.assign(allScopes, selectedScopes);
    }

    // Add custom scopes
    customScopes.forEach(({ name, value }) => {
      if (name) allScopes[name] = value;
    });

    return allScopes;
  };

  // Checks if no valid facts/scopes are selected
  const areFactsEmpty = () => {
    if (!selectedExperienceTypes || selectedExperienceTypes.length === 0) {
      return false;
    }
    // For all custom types, check if any valid custom scopes exist
    const allCustom = selectedExperienceTypes.every(
      (type) => type.id === 'experience.custom'
    );

    if (allCustom) {
      return customScopes.every(
        (scope) => !scope.name.trim() || !scope.value.trim()
      );
    }

    // For mixed types, check both predefined and custom scopes
    const hasPredefinedScopes = Object.keys(selectedScopes || {}).length > 0;
    const hasCustomScopes = customScopes.some(
      (scope) => scope.name.trim() && scope.value.trim()
    );

    return !hasPredefinedScopes && !hasCustomScopes;
  };

  return {
    factsLoading,
    selectedExperienceTypes,
    setSelectedExperienceTypes,
    experienceTypeSelected,
    setExperienceTypeSelected,
    predefinedScopes,
    setPredefinedScopes,
    selectedScopes,
    setSelectedScopes,
    availableScopes,
    setAvailableScopes,
    customScopes,
    setCustomScopes,
    handleMakeCustom,
    handleDeleteScope,
    mergeAllScopes,
    areFactsEmpty,
  };
};
